import React, {useEffect} from "react";
import ControlBarDropDown from "./ControlBarDropDown";
import ControlBarDropDownMenuItem from "./ControlBarDropDownMenuItem";
import ISelectOption from "../../models/view/common/ISelectOption";
import DemoMode from "../../models/view/lessonPage/DemoMode";
import '../../styles/components/lesson/demo-mode-selector.scss';
import {DemoModeSelectorIcon} from "../../modules/lesson/demo-mode-selector-icon/DemoModeSelectorIcon";
import {useAppSelector} from "../../redux/hooks";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import Toggle from "../common/Toggle";
import playbackOrchestrator from "../../services/playbackOrchestrator/playbackOrchestrator";
import {getDemoModeIcons} from "../../modules/lesson/demo-mode-selector-icon/DemoModeSelectorIcon.logic";
import settingsService from "../../services/settings/settingsService";
import ControlBarMenuItemGroup from "./ControlBarMenuItemGroup";
import {SegmentModeEnum} from "../../models/view/lessonPage/SegmentModeEnum";
import {KeyboardHighlightsIcon} from "./keyboardHighlightsIcon/KeyboardHighlightsIcon";
import {settingsStore} from "../../redux/settings/settingsStore";
import {defaultVolumes} from "../../redux/settings/settingsDefaults";

export default function DemoModeSelector() {
  const isDemoModeDropdownSelectorOpen = useAppSelector(lessonPlayerStore.getIsDemoSelectorDropdownOpen);
  const demoModeSelectOptions = useAppSelector(lessonPlayerStore.getDemoModeSelectOptions);
  const demoMode = useAppSelector(lessonPlayerStore.getDemoMode);
  const isDemoSelectorEnabled = useAppSelector(lessonPlayerStore.getIsDemoSelectorEnabled);
  const isDemoLeftHandSoundEnabled = useAppSelector(lessonPlayerStore.getIsDemoLeftHandSoundEnabled);
  const isDemoRightHandSoundEnabled = useAppSelector(lessonPlayerStore.getIsDemoRightHandSoundEnabled);
  const isDisabled = !isDemoSelectorEnabled;
  const demoSoundOptions = createDemoSoundOptions(isDemoLeftHandSoundEnabled, isDemoRightHandSoundEnabled);
  const demoVisualOptions = createDemoVisualOptions(demoModeSelectOptions, demoMode);
  const dynamicHandDemoLabel = getDemoModeIcons(demoMode, isDemoLeftHandSoundEnabled, isDemoRightHandSoundEnabled).dynamicLabel;

  // Do some things when demo mode or demo sound is changed.
  useEffect(() => {
    // Some effects of changing demo mode or demo sound are currently coupled to internals of the playback orchestrator.
    playbackOrchestrator.onDemoModeOrDemoSoundEnabled_changed().then(() => {
      // NOTE: resetPianoDemoSchedule call depends on changes made during onDemoModeOrDemoSoundEnabled_changed

      // Reset the piano demo schedule in the web app, if applicable.
      const isPlaying = lessonPlayerStore.getIsPlaying();
      const isCountInPlaying = lessonPlayerStore.getIsCountInPlaying();

      if ((isPlaying || isCountInPlaying) && !playbackOrchestrator.isLoopDefined()) {
        void playbackOrchestrator.resetPianoDemoSchedule();
      }
    });
  }, [demoMode, isDemoRightHandSoundEnabled, isDemoLeftHandSoundEnabled]);

  return (
    <ControlBarDropDown
      additionalCssClassName="demo-mode-dropdown-selector"
      tooltip={dynamicHandDemoLabel}
      ariaLabel={dynamicHandDemoLabel}
      isDropdownMenuOpen={isDemoModeDropdownSelectorOpen}
      changeDropdownMenuOpen={(isOpen: boolean) => {
        lessonPlayerStore.setIsDemoSelectorDropdownOpen(isOpen);
      }}
      iconElement={<DemoModeSelectorIcon />}
      disabled={isDisabled}
    >
      {demoSoundOptions}
      <ControlBarMenuItemGroup label={"Keyboard Highlights"}>
        {demoVisualOptions}
      </ControlBarMenuItemGroup>
    </ControlBarDropDown>
  );
}

function changeDemoMode(demoMode: DemoMode) {
  lessonPlayerStore.setDemoMode(demoMode);
}

function createDemoSoundOptions(
  isDemoLeftHandSoundEnabled: boolean,
  isDemoRightHandSoundEnabled: boolean) {
  return <ControlBarMenuItemGroup label={"Piano Sound Demos"}>
    <ControlBarDropDownMenuItem>
      <Toggle
        label={"Right Hand Sound"}
        isToggledOn={isDemoRightHandSoundEnabled}
        onToggle_toggled={() => {
          toggleRightHandDemoSound();
        }}
      />
    </ControlBarDropDownMenuItem>
    <ControlBarDropDownMenuItem>
      <Toggle
        label={"Left Hand Sound"}
        isToggledOn={isDemoLeftHandSoundEnabled}
        onToggle_toggled={() => {
          toggleLeftHandDemoSound();
        }}
      />
    </ControlBarDropDownMenuItem>
  </ControlBarMenuItemGroup>
}

function toggleLeftHandDemoSound() {
  const wasPitchDetectionEnabledPreChange = lessonPlayerStore.getIsPitchDetectionEnabled();

  const isDemoLeftHandSoundEnabledAfterToggle = !lessonPlayerStore.getIsDemoLeftHandSoundEnabled();

  if (isDemoLeftHandSoundEnabledAfterToggle && (settingsStore.getPianoVolume() === 0)) {
    // If the piano volume is 0, set the piano volume to the default value so user can hear the demo sound.
    settingsStore.setPianoVolume(defaultVolumes.piano);
  }

  lessonPlayerStore.setIsDemoLeftHandSoundEnabled(isDemoLeftHandSoundEnabledAfterToggle);

  lessonPlayerStore.setCanShowDemoSoundActivatedPrompt(wasPitchDetectionEnabledPreChange && isDemoLeftHandSoundEnabledAfterToggle);
}

function toggleRightHandDemoSound() {
  const wasPitchDetectionEnabledPreChange = lessonPlayerStore.getIsPitchDetectionEnabled();

  const isDemoRightHandSoundEnabledAfterToggle = !lessonPlayerStore.getIsDemoRightHandSoundEnabled();

  if (isDemoRightHandSoundEnabledAfterToggle && (settingsStore.getPianoVolume() === 0)) {
    // If the piano volume is 0, set the piano volume to the default value so user can hear the demo sound.
    settingsStore.setPianoVolume(defaultVolumes.piano);
  }

  lessonPlayerStore.setIsDemoRightHandSoundEnabled(isDemoRightHandSoundEnabledAfterToggle);

  lessonPlayerStore.setCanShowDemoSoundActivatedPrompt(wasPitchDetectionEnabledPreChange && isDemoRightHandSoundEnabledAfterToggle);
}

function createDemoVisualOptions(
  options: ISelectOption<DemoMode>[],
  currentlySelectedDemoMode: DemoMode) {
  const isPlaying = lessonPlayerStore.getIsPlaying();
  const isCountInPlaying = lessonPlayerStore.getIsCountInPlaying();
  const hasPlaybackStarted = isPlaying || isCountInPlaying;

  return options.map(o => createDemoVisualOption(o, currentlySelectedDemoMode, hasPlaybackStarted));
}

function createDemoVisualOption(
  option: ISelectOption<DemoMode>,
  currentlySelectedDemoMode: DemoMode,
  hasPlaybackStarted: boolean) {
  const isSelected = option.value === currentlySelectedDemoMode;

  return <ControlBarDropDownMenuItem
    key={`mode_${option.value}`}
    isSelected={isSelected}
    onClick={() => { onDemoModeVisualOption_selected(option, hasPlaybackStarted); }}>
    <div className={'demoHandOption'}>
      <div className={'demoVisualOptionLabel'}>{option.label}</div>
      <div className={'demoHandOptionIcons'}>
        <KeyboardHighlightsIcon highlights={`${option.value}`} />
      </div>
    </div>
  </ControlBarDropDownMenuItem>;
}

function onDemoModeVisualOption_selected(
  option: ISelectOption<DemoMode>,
  hasPlaybackStarted: boolean): void {

  changeDemoMode(option.value);

  const showVirtualKeyboardIfCurrentlyHidden = (mode: DemoMode) => {
    if (mode !== 'off') {
      lessonPlayerStore.setFretboardIsVisible(true);

      const segment = lessonPlayerStore.getSegment();
      if (segment) {
        switch (segment.mode) {
          case SegmentModeEnum.followAlong:
            settingsService.partiallySetUserPreferencesForControlBar({isBirdsEyeKeyboardEnabledForSegmentsWithBothVideoAndNotation: true});
            break;
          case SegmentModeEnum.play:
            settingsService.partiallySetUserPreferencesForControlBar({isBirdsEyeKeyboardEnabledForSegmentsWithVideoOnly: true});
            break;
          default:
            settingsService.partiallySetUserPreferencesForControlBar({isBirdsEyeKeyboardEnabled: true});
        }
      }
    }
  };

  if (hasPlaybackStarted) {
    playbackOrchestrator.resetPianoDemoSchedule().then(() => {
      showVirtualKeyboardIfCurrentlyHidden(option.value);
    });
  } else {
    showVirtualKeyboardIfCurrentlyHidden(option.value);
  }
}
